import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useUnits, useUnitsCategories } from '../../../hooks/useUnits';
import axiosInstance from '../../../utils/api';

/**
 * Custom hook สำหรับจัดการ Units Management
 * รวม CRUD operations, Search, Filter, Pagination
 */
export const useUnitsManagement = () => {
  // State สำหรับ table
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  
  // State สำหรับ pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ['10', '25', '50', '100'],
    showTotal: (total, range) => `${range[0]}-${range[1]} จาก ${total} รายการ`
  });
  
  // State สำหรับ filters
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    is_active: null
  });
  
  // State สำหรับ sorter
  const [sorter, setSorter] = useState({
    field: 'display_order',
    order: 'ascend'
  });

  // ใช้ hooks ที่มีอยู่แล้ว
  const { units, refreshUnits } = useUnits({ 
    activeOnly: false, 
    autoFetch: false 
  });
  
  const { categories, refreshCategories } = useUnitsCategories();

  // State สำหรับข้อมูลที่แสดงในตาราง
  const [tableData, setTableData] = useState([]);

  /**
   * โหลดข้อมูลหน่วยนับพร้อม pagination และ filters
   */
  const loadUnits = useCallback(async (params = {}) => {
    setLoading(true);
    
    try {
      const requestParams = {
        page: params.page || pagination.current,
        limit: params.limit || pagination.pageSize,
        search: params.search || filters.search,
        category: params.category || filters.category,
        is_active: params.is_active !== undefined ? params.is_active : filters.is_active,
        sort_field: params.sort_field || sorter.field,
        sort_order: params.sort_order || (sorter.order === 'ascend' ? 'asc' : 'desc')
      };

      // ลบ parameter ที่เป็น null หรือ empty string
      Object.keys(requestParams).forEach(key => {
        if (requestParams[key] === null || requestParams[key] === '') {
          delete requestParams[key];
        }
      });

      const response = await axiosInstance.get('/api/units', { params: requestParams });
      
      if (response.data.success) {
        const { data, total, page, limit } = response.data;
        
        setTableData(data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: limit,
          total: total
        }));
        
        return { data, total };
      } else {
        throw new Error(response.data.message || 'Failed to load units');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลหน่วยนับ';
      message.error(errorMessage);
      return { data: [], total: 0 };
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters, sorter]);

  /**
   * สร้างหน่วยนับใหม่
   */
  const createUnit = useCallback(async (unitData) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/units', unitData);
      
      if (response.data.success) {
        message.success('เพิ่มหน่วยนับใหม่สำเร็จ');
        loadUnits(); // รีโหลดข้อมูล
        refreshUnits(); // อัปเดต cache
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create unit');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการเพิ่มหน่วยนับ';
      message.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadUnits, refreshUnits]);

  /**
   * อัปเดตหน่วยนับ
   */
  const updateUnit = useCallback(async (unitId, unitData) => {
    setLoading(true);
    try {
      const response = await axiosInstance.put(`/api/units/${unitId}`, unitData);
      
      if (response.data.success) {
        message.success('แก้ไขข้อมูลหน่วยนับสำเร็จ');
        loadUnits(); // รีโหลดข้อมูล
        refreshUnits(); // อัปเดต cache
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update unit');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการแก้ไขหน่วยนับ';
      message.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadUnits, refreshUnits]);

  /**
   * ลบหน่วยนับ (soft delete)
   */
  const deleteUnit = useCallback(async (unitId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.delete(`/api/units/${unitId}`);
      
      if (response.data.success) {
        message.success('ลบหน่วยนับสำเร็จ');
        loadUnits(); // รีโหลดข้อมูล
        refreshUnits(); // อัปเดต cache
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to delete unit');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการลบหน่วยนับ';
      message.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadUnits, refreshUnits]);

  /**
   * ลบหน่วยนับหลายรายการ
   */
  const deleteMultipleUnits = useCallback(async (unitIds) => {
    setLoading(true);
    try {
      const promises = unitIds.map(id => axiosInstance.delete(`/api/units/${id}`));
      await Promise.all(promises);
      
      message.success(`ลบหน่วยนับ ${unitIds.length} รายการสำเร็จ`);
      loadUnits(); // รีโหลดข้อมูล
      refreshUnits(); // อัปเดต cache
      setSelectedRowKeys([]); // ล้างการเลือก
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาดในการลบหน่วยนับ';
      message.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadUnits, refreshUnits]);

  /**
   * จัดการ filter change
   */
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, current: 1 })); // รีเซ็ตไปหน้าแรก
  }, []);

  /**
   * จัดการ table change (pagination, sorter, filters)
   */
  const handleTableChange = useCallback((newPagination, tableFilters, newSorter) => {
    setPagination(prev => ({ ...prev, ...newPagination }));
    
    if (newSorter && newSorter.field) {
      setSorter({
        field: newSorter.field,
        order: newSorter.order || 'ascend'
      });
    }
  }, []);

  /**
   * รีเซ็ตฟิลเตอร์ทั้งหมด
   */
  const resetFilters = useCallback(() => {
    setFilters({
      search: '',
      category: '',
      is_active: null
    });
    setSorter({
      field: 'display_order',
      order: 'ascend'
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  /**
   * รีเฟรชข้อมูลทั้งหมด
   */
  const refreshData = useCallback(() => {
    loadUnits();
    refreshUnits();
    refreshCategories();
  }, [loadUnits, refreshUnits, refreshCategories]);

  // โหลดข้อมูลเมื่อ dependencies เปลี่ยน
  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  // คำนวณสถิติ
  const statistics = {
    total: pagination.total,
    active: tableData.filter(unit => unit.is_active).length,
    inactive: tableData.filter(unit => !unit.is_active).length,
    categories: categories.length
  };

  return {
    // Data
    tableData,
    categories,
    statistics,
    
    // UI State
    loading,
    pagination,
    filters,
    sorter,
    selectedRowKeys,
    
    // Actions
    loadUnits,
    createUnit,
    updateUnit,
    deleteUnit,
    deleteMultipleUnits,
    refreshData,
    
    // Handlers
    handleFilterChange,
    handleTableChange,
    resetFilters,
    setSelectedRowKeys
  };
};